/** DOMExceptions thrown when a mutation targets a node that isn't where the caller thinks. */
const DOM_DESYNC_ERROR_NAMES = ['NotFoundError', 'HierarchyRequestError'];

/**
 * The mutation methods that raise those names. Chrome/Edge ("Failed to execute
 * 'removeChild' on 'Node': …") and Firefox ("Node.removeChild: …") both name the method.
 */
const DOM_MUTATION_METHOD = /removeChild|insertBefore|appendChild|replaceChild/;

/**
 * WebKit does NOT name the method — every `NotFoundError` gets the same generic string —
 * so requiring `DOM_MUTATION_METHOD` alone would silently exclude Safari and every iOS
 * browser from this bucket. That is the wrong half of the population to miss: those are
 * the ones where the JS stack is least informative to begin with.
 *
 * The cost is precision. WebKit gives us no way to tell this apart from an unrelated
 * `NotFoundError`, so a Safari `Range` or `IndexedDB` failure can land here too. Worth
 * it — a slightly noisy bucket beats a bucket that appears empty on a whole platform.
 */
const WEBKIT_GENERIC_NOT_FOUND = /object can ?not be found here/i;

/**
 * Returns true when `error` looks like React (or any other DOM owner) trying to
 * mutate a node that has been moved out from under it — the
 * `"NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed
 * is not a child of this node"` family.
 *
 * Why this deserves its own tag: the JS stack for these is 100% react-dom internals
 * (`commitDeletionEffects` and friends), so it symbolicates to nothing useful, and
 * the React `componentStack` that WOULD identify the component is stored by Sentry as
 * an opaque string it never source-maps. Grouping them under one `crashKind` is what
 * makes the class visible at all — and pairing it with `countTranslatorFontWrappers()`
 * usually identifies the cause in a single glance.
 *
 * The known causes are all "something reparented a node React owns":
 * - An in-page translator wrapping text nodes in `<font>` (see `pageTranslation.ts`).
 *   This is the one that crashed the library header.
 * - A browser extension rewriting the DOM.
 * - Our own code calling `replaceChild`/`removeChild` on a React-rendered node.
 *
 * Detection is `name` first, message second: `name` is the spec-defined, non-localised
 * part, while the message wording is browser-specific. Requiring a mutation method in
 * the message keeps unrelated `NotFoundError`s (which several other DOM APIs also
 * throw) out of the bucket.
 *
 * Duck-typed rather than `instanceof Error` on purpose. These are `DOMException`s, which
 * only gained `Error` in their prototype chain in ~2018, and anything that re-wraps or
 * structured-clones the value across a boundary loses the prototype too. Since the whole
 * job of this function is to notice a rare production crash, a false negative costs more
 * than the looser check does.
 */
export function errorIsDomDesync(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (typeof name !== 'string' || typeof message !== 'string') return false;
  if (!DOM_DESYNC_ERROR_NAMES.includes(name)) return false;
  return DOM_MUTATION_METHOD.test(message) || WEBKIT_GENERIC_NOT_FOUND.test(message);
}
